// src/layout/builders/ProjectLive/LiveTable.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useProjectStatus from "../../../store/builders/useProjectStatus";
import ProjectTable from "../ProjectTable";
import { toast } from "react-hot-toast";
import { UploadCloud, XCircle } from "lucide-react";
import SuccessPopup from "../../../components/common/SuccessPopup";
import useAuthStore from "../../../store/useAuthStore";

const allowedStatusesForLive = ["hold", "rejected", "completion_request"];

const LiveTable = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isIncharge = user?.role === "incharge";
  const {
    projects,
    isLoading,
    fetchProjects,
    updateProjectStatus,
    checkCompletionDocumentsStatus,
  } = useProjectStatus();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [missingDocsForModal, setMissingDocsForModal] = useState([]);
  const [paymentDetailsForModal, setPaymentDetailsForModal] = useState(null);
  const [modalProjectId, setModalProjectId] = useState(null);
  const [modalType, setModalType] = useState("docs"); // "docs" or "payment" or "both"

  // --- Success Popup State ---
  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
  const [successPopupMessage, setSuccessPopupMessage] = useState("");
  const [redirectPath, setRedirectPath] = useState(""); // Store path for redirection
  // --- End Success Popup State ---

  useEffect(() => {
    fetchProjects("active");
  }, [fetchProjects]);

  const handleStatusChange = async (projectId, newStatus, comment = null) => {
    if (newStatus === "completion_request") {
      const docStatusResult = await checkCompletionDocumentsStatus(projectId);
      console.log(`[DEBUG] docStatusResult for project ${projectId}:`, docStatusResult);
      if (!docStatusResult) {
        toast.error("Could not verify document status. Please try again.");
        return { success: false };
      }
      if (docStatusResult.allUploaded && docStatusResult.isFullyPaid) {
        console.log(`[DEBUG] Project ${projectId} is eligible. Proceeding with status change to ${newStatus}...`);
        const updateResult = await updateProjectStatus(projectId, newStatus);
        if (updateResult?.success) {
          // Show success popup and set redirect path
          setSuccessPopupMessage("Completion request submitted successfully!");
          setRedirectPath("/builder/projects?tab=completion_request");
          setIsSuccessPopupOpen(true);
        }
        return updateResult;
      } else {
        // Show Missing Docs/Payment Modal
        const missing = docStatusResult.documents
          .filter((doc) => doc.required && !doc.uploaded)
          .map((doc) => doc.name);
        
        setMissingDocsForModal(missing);
        setPaymentDetailsForModal(docStatusResult.paymentDetails);
        setModalProjectId(projectId);
        
        if (!docStatusResult.allUploaded && !docStatusResult.isFullyPaid) {
          setModalType("both");
        } else if (!docStatusResult.allUploaded) {
          setModalType("docs");
        } else {
          setModalType("payment");
        }
        
        setShowUploadModal(true);
        return { success: false, reason: "validation_failed" };
      }
    } else {
      // Handle other statuses (hold, rejected) *after* confirmation (in StatusChangePopup)
      const result = await updateProjectStatus(projectId, newStatus, comment);
      if (result?.success) {
        // Show success popup and set redirect path
        if (newStatus === "rejected") {
          setSuccessPopupMessage(
            "Project status successfully updated to Rejected."
          );
          setRedirectPath("/builder/projects?tab=rejected");
          setIsSuccessPopupOpen(true);
        } else if (newStatus === "hold") {
          // No success popup for hold, just navigate directly
          navigate("/builder/projects?tab=hold");
        } else {
          fetchProjects("active"); // Refresh for other statuses if needed
        }
      }
      return result;
    }
  };

  const handleNavigateToUpload = () => {
    if (modalProjectId) {
      navigate(`/upload-doc/${modalProjectId}`);
      setShowUploadModal(false);
      setModalProjectId(null);
      setMissingDocsForModal([]);
    }
  };

  const handleNavigateToFinances = () => {
    if (modalProjectId) {
      navigate(`/builder/project/${modalProjectId}/detail?tab=finances&subtab=payment`);
      setShowUploadModal(false);
      setModalProjectId(null);
      setMissingDocsForModal([]);
    }
  };

  // --- Close Success Popup and Redirect ---
  const closeSuccessPopupAndRedirect = () => {
    setIsSuccessPopupOpen(false);
    if (redirectPath) {
      navigate(redirectPath);
      setRedirectPath(""); // Clear path after navigation
    }
  };

  if (isLoading && projects.length === 0) {
    // ... (loading spinner remains the same)
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      {/* Success Popup */}
      <SuccessPopup
        isOpen={isSuccessPopupOpen}
        onClose={closeSuccessPopupAndRedirect}
        message={successPopupMessage}
      />

      {/* Upload Documents Required Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          {/* ... (Upload Modal content remains the same) ... */}
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full text-center transform transition-all scale-100 opacity-100">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {modalType === "both" ? "Documents & Payment Required" :
               modalType === "docs" ? "Upload Required Documents" : 
               "Payment Required"}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {modalType === "both" ? "To request project completion, please upload all documents and ensure total project value is paid." :
               modalType === "docs" ? "To request project completion, please upload all required documents first." :
               "To request project completion, the total project value must be fully paid by the customer."}
            </p>
            
            {/* List of Missing Documents */}
            {(modalType === "docs" || modalType === "both") && missingDocsForModal.length > 0 && (
              <div className="text-left mb-4 bg-red-50 p-3 rounded-md border border-red-200">
                <p className="text-xs font-bold text-red-700 mb-1">
                  Missing Documents:
                </p>
                <ul className="list-disc list-inside text-xs text-red-600 space-y-0.5">
                  {missingDocsForModal.map((docName, index) => (
                    <li key={index}>{docName}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Payment Details */}
            {(modalType === "payment" || modalType === "both") && paymentDetailsForModal && (
              <div className="text-left mb-4 bg-orange-50 p-3 rounded-md border border-orange-200">
                <p className="text-xs font-bold text-orange-700 mb-2">
                  Payment Status:
                </p>
                <div className="space-y-1 text-xs text-orange-600">
                  <div className="flex justify-between">
                    <span>Total Project Value:</span>
                    <span className="font-semibold">₹{paymentDetailsForModal.totalBudget?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Paid by Customer:</span>
                    <span className="font-semibold">₹{paymentDetailsForModal.totalPaid?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between border-t border-orange-200 pt-1 mt-1 font-bold text-sm">
                    <span>Balance Due:</span>
                    <span className="text-red-600">₹{paymentDetailsForModal.balance?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}
            {/* Modal Action Buttons */}
            <div className="flex justify-center flex-wrap gap-4 mt-5">
              <button
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium min-w-[100px]"
              >
                Cancel
              </button>
              
              {(modalType === "docs" || modalType === "both") && (
                <button
                  onClick={handleNavigateToUpload}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium flex items-center min-w-[120px] justify-center"
                >
                  <UploadCloud className="w-4 h-4 mr-1.5" />
                  Upload Now
                </button>
              )}

              {(modalType === "payment" || modalType === "both") && (
                <button
                  onClick={handleNavigateToFinances}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium flex items-center min-w-[140px] justify-center"
                >
                  Go to Finances
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <ProjectTable
        title={isIncharge ? "My Projects" : "Live Projects"}
        projects={projects.filter((p) => {
          if (!isIncharge) return true;
          const assigned = (user?.assigned_projects || user?.assignedProjects || []).map(Number);
          return assigned.includes(Number(p.id));
        })}
        handleStatusChange={handleStatusChange} // Pass the updated handler
        navigate={navigate}
        allowedNextStatuses={allowedStatusesForLive}
        showActionsColumn={true}
      />
    </>
  );
};

export default LiveTable;
